declare global {
  interface Window {
    grecaptcha?: {
      ready(callback: () => void): void;
      execute(siteKey: string, options: { action: string }): Promise<string>;
    };
  }
}

const RECAPTCHA_SRC = "https://www.google.com/recaptcha/api.js?render=";
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? "";

let loadPromise: Promise<void> | null = null;

const ensureScriptLoaded = (): Promise<void> => {
  if (!SITE_KEY) {
    return Promise.reject(new Error("Recaptcha site key not configured."));
  }

  if (typeof window === "undefined") {
    return Promise.reject(new Error("Recaptcha unavailable on server side."));
  }

  if (window.grecaptcha) {
    // Script already present
    return Promise.resolve();
  }

  if (!loadPromise) {
    loadPromise = new Promise<void>((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>(
        `script[src^="${RECAPTCHA_SRC}"]`
      );

      if (existingScript) {
        existingScript.addEventListener("load", () => resolve());
        existingScript.addEventListener("error", () =>
          reject(new Error("No se pudo cargar reCAPTCHA."))
        );
        return;
      }

      const script = document.createElement("script");
      script.src = `${RECAPTCHA_SRC}${encodeURIComponent(SITE_KEY)}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error("No se pudo cargar reCAPTCHA."));
      document.head.appendChild(script);
    });
  }

  return loadPromise;
};

export const isRecaptchaEnabled = (): boolean => Boolean(SITE_KEY);

export const getRecaptchaToken = async (action: string): Promise<string> => {
  await ensureScriptLoaded();

  if (!window.grecaptcha) {
    throw new Error("reCAPTCHA no está disponible.");
  }

  await new Promise<void>((resolve) => window.grecaptcha!.ready(resolve));

  return window.grecaptcha.execute(SITE_KEY, { action });
};
